import { ChartConfig } from "@/components/chart";

export const bucketLabels: Record<string, string> = {
  "0_1h": "0-1h",
  "1_6h": "1-6h",
  "6_24h": "6-24h",
  "1_7d": "1-7d",
  over_7d: ">7d",
  negative: "Negative",
  no_timestamp: "No TS",
};

export const bucketOrder: Record<string, number> = {
  negative: 0,
  "0_1h": 1,
  "1_6h": 2,
  "6_24h": 3,
  "1_7d": 4,
  over_7d: 5,
  no_timestamp: 6,
};

export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  } else if (seconds < 3600) {
    return `${Math.round(seconds / 60)}m`;
  } else if (seconds < 86400) {
    return `${(seconds / 3600).toFixed(1)}h`;
  } else {
    return `${(seconds / 86400).toFixed(1)}d`;
  }
}

export const primaryHistogramConfig = {
  percentage: {
    label: "% of AP Conversions",
    color: "hsl(var(--chart-1))",
  },
} satisfies ChartConfig;

export const secondaryHistogramConfig = {
  percentage: {
    label: "% of 2nd AP Conversions",
    color: "hsl(var(--chart-1))",
  },
} satisfies ChartConfig;

type CustomTooltipContentProps = {
  active?: boolean;
  payload?: any[];
  label?: string;
  labelFormatter?: (label: string) => string;
  config: ChartConfig;
};

export const CustomTooltipContent = ({ active, payload, label, labelFormatter, config }: CustomTooltipContentProps) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
      {labelFormatter && label && <div className="font-medium">{labelFormatter(label)}</div>}
      <div className="grid gap-1.5">
        {payload.map((item: any, index: number) => {
          const value = item.value;
          const dataKey = item.dataKey;
          const configLabel = config[dataKey]?.label || item.name || dataKey;

          let displayValue;
          if (value === undefined || value === null) {
            displayValue = "No data";
          } else if (dataKey && (dataKey.includes("Percentage") || dataKey.includes("percentage"))) {
            displayValue = `${value.toFixed(1)}%`;
          } else {
            displayValue = value.toLocaleString();
          }

          return (
            <div key={item.dataKey || index} className="flex w-full flex-wrap items-center gap-2">
              <div
                className="h-2.5 w-2.5 shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg]"
                style={
                  {
                    "--color-bg": item.color,
                    "--color-border": item.color,
                  } as React.CSSProperties
                }
              />
              <div className="flex flex-1 justify-between leading-none items-center">
                <div className="grid gap-1.5">
                  <span className="text-muted-foreground">{configLabel}</span>
                </div>
                <span className="font-mono font-medium tabular-nums text-foreground">{displayValue}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

type Threshold = {
  GOOD: number;
  MODERATE: number;
};

export const getColorFromThreshold = (
  metric: number,
  threshold: Threshold,
  lowerValueIsBetter: boolean = false,
): string => {
  if (lowerValueIsBetter) {
    if (metric <= threshold.GOOD) return "text-green-600";
    if (metric <= threshold.MODERATE) return "text-yellow-600";
  } else {
    if (metric >= threshold.GOOD) return "text-green-600";
    if (metric >= threshold.MODERATE) return "text-yellow-600";
  }
  return "text-red-600";
};
